import { type ReactNode, useMemo } from 'react';
import { BarStripes, type BarStripe } from './bar-stripes';
import { BarHeader } from './bar-header';
import { BarSegmentView, type BarSegment } from './bar-segment';

export type { BarSegment, BarStripe };

type Props = {
  segments: BarSegment[];
  total: number;
  goal: number;
  label: string;
  heightClass?: string;
  stripes?: BarStripe[];
};

type TrackProps = {
  bars: { seg: BarSegment; pct: number }[];
  stripes: BarStripe[];
  heightClass: string;
  barGoal: number;
};

function BarTrack(p: TrackProps): ReactNode {
  return (
    <div
      className={`relative flex ${p.heightClass} overflow-visible rounded-lg bg-white/[0.06]`}
    >
      {p.bars.map(({ seg, pct }) => (
        <BarSegmentView key={seg.id} seg={seg} pct={pct} barGoal={p.barGoal} />
      ))}
      <BarStripes stripes={p.stripes} />
    </div>
  );
}

function hasMilestoneLabels(stripes: BarStripe[]): boolean {
  return stripes.some((s) => s.variant === 'milestone' && !!s.label);
}

export function StackedProgressBar({
  segments,
  total,
  goal,
  label,
  heightClass = 'h-3',
  stripes = [],
}: Props): ReactNode {
  const bars = useMemo(
    () => segments.map((s) => ({ seg: s, pct: (s.value / goal) * 100 })),
    [segments, goal]
  );
  const pad = hasMilestoneLabels(stripes) ? 'pb-5' : '';
  return (
    <div className={`space-y-1.5 ${pad}`}>
      <BarHeader label={label} total={total} goal={goal} />
      <BarTrack
        bars={bars}
        stripes={stripes}
        heightClass={heightClass}
        barGoal={goal}
      />
    </div>
  );
}
